import { useState, useEffect } from 'react';
import axios from 'axios';
import Hero from '../components/Hero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FindCoach = () => {
  const [coaches, setCoaches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    specialty: 'All Specialties',
    tier: 'All Evidence Tiers',
    credential: 'All Credential Levels'
  });

  const specialties = [
    'All Specialties',
    'Mindfulness',
    'NLP Coaching',
    'Emotional Intelligence',
    'Spiritual Coaching',
    'Somatic Coaching',
    'CBT Coaching',
    'Executive Coaching',
    'Breathwork',
    'Positive Psychology',
    'Sound Healing',
    'Vedantic Coaching'
  ];

  const tiers = [
    'All Evidence Tiers',
    'Tier 1',
    'Tier 2',
    'Tier 3'
  ];

  const credentials = [
    'All Credential Levels',
    'MAC',
    'PAC',
    'AAC'
  ];

  useEffect(() => {
    fetchCoaches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const fetchCoaches = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.specialty !== 'All Specialties') params.append('specialty', filters.specialty);
      if (filters.tier !== 'All Evidence Tiers') params.append('tier', filters.tier);
      if (filters.credential !== 'All Credential Levels') params.append('credential', filters.credential);
      
      const response = await axios.get(`${API}/coaches?${params.toString()}`);
      setCoaches(response.data);
    } catch (error) {
      console.error('Error fetching coaches:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierBadgeColor = (tier) => {
    if (tier === 'Tier 1') return 'bg-green-100 text-green-800';
    if (tier === 'Tier 2') return 'bg-yellow-100 text-yellow-800';
    if (tier === 'Tier 3') return 'bg-purple-100 text-purple-800';
    return 'bg-gray-100 text-gray-800';
  };

  const getCredentialBadgeColor = (credential) => {
    if (credential === 'MAC') return 'bg-blue-600 text-white';
    if (credential === 'PAC') return 'bg-blue-500 text-white';
    if (credential === 'AAC') return 'bg-blue-400 text-white';
    return 'bg-gray-500 text-white';
  };

  return (
    <div>
      <Hero
        title="Find an ICCAC Accredited Coach"
        subtitle="Every coach has been independently assessed, trained in evidence literacy, and committed to the ICCAC Code of Ethics"
      />

      <div className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="bg-white p-6 rounded-lg shadow-md mb-8">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Search Filters</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Specialty</label>
                <select
                  value={filters.specialty}
                  onChange={(e) => setFilters({ ...filters, specialty: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent appearance-none bg-white"
                  data-testid="specialty-filter"
                >
                  <option value="All Specialties">All Specialties</option>
                  <option value="Mindfulness">Mindfulness</option>
                  <option value="NLP Coaching">NLP Coaching</option>
                  <option value="Emotional Intelligence">Emotional Intelligence</option>
                  <option value="Spiritual Coaching">Spiritual Coaching</option>
                  <option value="Somatic Coaching">Somatic Coaching</option>
                  <option value="CBT Coaching">CBT Coaching</option>
                  <option value="Executive Coaching">Executive Coaching</option>
                  <option value="Breathwork">Breathwork</option>
                  <option value="Positive Psychology">Positive Psychology</option>
                  <option value="Sound Healing">Sound Healing</option>
                  <option value="Vedantic Coaching">Vedantic Coaching</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Evidence Tier</label>
                <select
                  value={filters.tier}
                  onChange={(e) => setFilters({ ...filters, tier: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent appearance-none bg-white"
                  data-testid="tier-filter"
                >
                  <option value="All Evidence Tiers">All Evidence Tiers</option>
                  <option value="Tier 1">Tier 1</option>
                  <option value="Tier 2">Tier 2</option>
                  <option value="Tier 3">Tier 3</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Credential Level</label>
                <select
                  value={filters.credential}
                  onChange={(e) => setFilters({ ...filters, credential: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#1e3a5f] focus:border-transparent appearance-none bg-white"
                  data-testid="credential-filter"
                >
                  <option value="All Credential Levels">All Credential Levels</option>
                  <option value="MAC">MAC</option>
                  <option value="PAC">PAC</option>
                  <option value="AAC">AAC</option>
                </select>
              </div>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="text-center py-12">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#1e3a5f]"></div>
              <p className="mt-4 text-gray-600">Loading coaches...</p>
            </div>
          ) : coaches.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-md">
              <p className="text-xl text-gray-600">No coaches found matching your criteria. Try adjusting your filters.</p>
            </div>
          ) : (
            <div>
              <div className="mb-6 text-gray-600">
                <span className="font-semibold">{coaches.length}</span> coaches found
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="coaches-grid">
                {coaches.map((coach) => (
                  <div key={coach.id} className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow" data-testid="coach-card">
                    <div className="flex items-center mb-4">
                      <div className="w-16 h-16 bg-[#1e3a5f] text-white rounded-full flex items-center justify-center text-2xl font-bold mr-4">
                        {coach.initials}
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-800">{coach.name}</h3>
                        <span className={`inline-block px-3 py-1 rounded-full text-sm font-bold ${getCredentialBadgeColor(coach.credential)}`}>
                          {coach.credential}
                        </span>
                      </div>
                    </div>
                    <div className="mb-3">
                      <div className="text-sm text-gray-600 mb-1">📍 {coach.location}</div>
                    </div>
                    <div className="mb-4">
                      <div className="text-sm font-semibold text-gray-700 mb-2">Specialties:</div>
                      <div className="flex flex-wrap gap-2">
                        {coach.specialties.map((specialty, index) => (
                          <span key={index} className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs">
                            {specialty}
                          </span>
                        ))}
                      </div>
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">Evidence Tiers:</div>
                      <div className="flex flex-wrap gap-2">
                        {coach.tiers.map((tier, index) => (
                          <span key={index} className={`px-2 py-1 rounded text-xs font-medium ${getTierBadgeColor(tier)}`}>
                            {tier}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* What to Expect */}
      <div className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8 text-center">What You Can Expect from Every ICCAC Coach</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700">They will tell you clearly, before you begin, what evidence tier the techniques they use are grounded in.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700">They will not make medical claims or promise clinical outcomes for coaching approaches.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700">They will refer you to a qualified clinical professional if your needs fall outside the scope of coaching.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700">They will maintain professional boundaries and keep your information confidential at all times.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700">They hold a verifiable credential that can be checked at iccacouncil.com at any time.</p>
            </div>
            <div className="flex items-start">
              <div className="flex-shrink-0 w-6 h-6 bg-green-500 rounded-full flex items-center justify-center mr-3 mt-1">
                <svg className="w-4 h-4 text-white" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                </svg>
              </div>
              <p className="text-gray-700">They have committed in writing to the ICCAC Code of Ethics across eight non-negotiable articles.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FindCoach;